/**
 * ProfileSettingsPage - Personal Profile Management
 * 
 * Manages user identity: name, photo, bio, location, birthday, sports.
 * Extracted from ProfileEditPage as part of settings consolidation.
 * 
 * Related Issues: #596, #598 (Settings/Edit page consolidation)
 */

import { useState, useEffect, useId, useRef, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../../lib/api/auth';
import { ApiError } from '../../lib/api';
import { useProfile, useUpdateProfile, useRemoveProfileImage } from '../../hooks/useProfile';
import { useProfileImageUpload } from '../../hooks/useProfileImageUpload';
import { TopBar, Button, Input, Skeleton, EmptyState } from '../../components';
import AvatarCropModal from '../../components/AvatarCropModal';
import { AvatarRemoveButton } from '../../components/AvatarRemoveButton';
import { Camera, User, MapPin, Calendar, Trophy, Save, X, AlertCircle, Mail, Smartphone, Check, Clock } from 'lucide-react';
import type { ProfileUpdateRequest, User as UserProfile } from '../../lib/api/profile';
import { normalizeClearableProfileFields } from '../../lib/api/profileFields';
import { PROFILE_SPORT_IDS, MAX_SECONDARY_SPORTS } from '../../data/sports';
import { useRequestPhoneVerification, useVerifyPhone, useRemovePhone, useRequestEmailVerification, useVerifyEmail } from '../../hooks/useProfile';
import { normalizePhoneE164 } from '../../lib/phone';
import { isSyntheticSmsEmail, isLikelyEmailShape, addEmailErrorMessage } from '../../lib/email';
import { timezoneAbbreviation, COMMON_TIMEZONES } from '../../constants/usStateTimezones';
import { toast } from 'sonner';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Canonical profile-sports list (#2698) — the SAME source the onboarding
// Activities step reads, kept in lockstep with backend ALLOWED_SPORTS by
// backend/tests/integration/sports-drift.test.ts. Renders the full-name `id`.
const ALLOWED_SPORTS = PROFILE_SPORT_IDS;

/**
 * Add-Email section for a phone-only STOP-lockout account (#2930).
 *
 * A phone-only signup has a synthetic `{phone}@sms.oro.fit` email with no
 * inbox, so the #1335 "Change Email" flow (which proves ownership of the
 * CURRENT address) can never complete for them. This is the recovery surface:
 * add a first REAL email via a 6-digit code sent to the NEW address, so the
 * magic-link sign-in path becomes usable. Mirrors PhoneSection's
 * idle -> input -> verify state machine and inline-error handling.
 *
 * Action layer (oro-ux): for a locked-out user this is the primary next thing,
 * so it renders as visible copy + a primary button, not a buried setting.
 * Errors surface inline with the backend's exact per-branch message so a user
 * can tell "that address is taken" from "you're going too fast" and self-serve.
 */
function AddEmailSection() {
  const [emailInput, setEmailInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [step, setStep] = useState<'idle' | 'input' | 'verify'>('idle');
  const [pendingEmail, setPendingEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Stable ids so each label/error binds to its own control (#2990 a11y). The
  // section renders once per page, but useId keeps that true if it ever doesn't.
  const emailFieldId = useId();
  const codeFieldId = useId();
  const errorId = useId();

  const requestMutation = useRequestEmailVerification();
  const verifyMutation = useVerifyEmail();

  /**
   * In-flight generation guard (#2990). Send Code, Resend, Verify and Cancel are
   * all reachable while a request is still in flight, and a resolving request
   * would otherwise apply its result over newer state: press Cancel and the
   * section returns to `idle`, then the in-flight Send Code resolves and yanks
   * the user back into `verify` for an address they abandoned — or a stale
   * failure paints an error onto a step it doesn't belong to.
   *
   * Every action that STARTS or ABANDONS a request bumps the generation; a
   * settled request applies nothing unless the generation it captured is still
   * current. A ref, not state: it must read current at await-resume time, and it
   * must not trigger a render of its own.
   */
  const generation = useRef(0);

  /**
   * Shared busy state across BOTH mutations (round 4 — Frank Medium, George
   * Low #1, convergent). The generation ref stops a stale result from being
   * APPLIED; it does not stop a second request from being STARTED. Gating each
   * button on only its own mutation left Send/Resend and Verify concurrent, and
   * `POST /profile/email/request` overwrites the same `email-verify:{userId}` KV
   * entry the verify reads — so a Resend pressed during an in-flight Verify
   * rotates the code out from under it and the user is told their correct code
   * is invalid. It also produced a swallowed error: Resend bumped the
   * generation, so the in-flight Verify's failure was discarded and the user sat
   * on the verify step with no feedback at all.
   *
   * Cancel is deliberately NOT gated on `busy`. This is a recovery surface: a
   * user who is already locked out must always be able to back out of it, and a
   * slow request is exactly when they will want to. The generation ref is what
   * makes that safe here — a late result finds a stale generation and applies
   * nothing, and web owns its errors inline so there is nothing left to fire.
   * (Native diverges; see the note on its `AddEmailSection`.)
   */
  const busy = requestMutation.isPending || verifyMutation.isPending;

  /** Abandon whatever is in flight and return to a clean step. */
  const resetTo = (next: 'idle' | 'input') => {
    generation.current += 1;
    setStep(next);
    setEmailError(null);
    if (next === 'idle') setCodeInput('');
    if (next === 'input') setEmailInput('');
  };

  const handleRequestCode = async () => {
    setEmailError(null);
    const candidate = emailInput.trim();
    if (!isLikelyEmailShape(candidate)) {
      setEmailError('Enter a valid email address');
      return;
    }
    const gen = ++generation.current;
    try {
      await requestMutation.mutateAsync(candidate);
      if (generation.current !== gen) return;
      setPendingEmail(candidate);
      // Clear any code already typed. On a RESEND the server has just rotated
      // `email-verify:{userId}`, so whatever is in the field is guaranteed
      // stale — leaving it there keeps Verify enabled on six digits that cannot
      // work, and submitting them burns one of only five attempts before the
      // token is destroyed. No-op on the first send.
      setCodeInput('');
      setStep('verify');
    } catch (err) {
      if (generation.current !== gen) return;
      setEmailError(
        err instanceof ApiError ? addEmailErrorMessage(err) : 'Failed to send code',
      );
    }
  };

  const handleVerifyCode = async () => {
    setEmailError(null);
    const gen = ++generation.current;
    try {
      await verifyMutation.mutateAsync(codeInput);
      // On success the profile's email is now real, so this whole section
      // unmounts and the change-email view takes its place. Nothing to reset.
    } catch (err) {
      if (generation.current !== gen) return;
      setEmailError(
        err instanceof ApiError ? addEmailErrorMessage(err) : 'Verification failed',
      );
    }
  };

  /**
   * Enter-key guards (#2990). This section renders INSIDE the page-wide profile
   * `<form>` (`:842`–`:1148`), which has its own submit button — so implicit
   * submission means Enter in either of these inputs saves the PROFILE instead
   * of advancing the recovery flow. A nested `<form>` is not the fix (illegal
   * HTML); intercepting the key on the input is.
   *
   * `preventDefault` runs BEFORE the busy/length check so a blocked Enter still
   * cannot fall through to the parent form, and each guard mirrors its button's
   * own `disabled` condition rather than inventing a second rule.
   *
   * Written as two plain handlers rather than one factory called in the JSX:
   * a factory invoked during render receives `handleRequestCode`, which reads
   * `generation.current`, and `react-hooks/refs` rejects that shape (the read is
   * only ever inside the event, but the rule cannot see that).
   */
  const handleEmailKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (busy) return;
    handleRequestCode();
  };

  const handleCodeKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (busy || codeInput.length !== 6) return;
    handleVerifyCode();
  };

  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
      <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
        <Mail className="w-5 h-5 text-accent" />
        Account
      </h2>

      {step === 'idle' && (
        <div>
          <p className="text-sm text-text-secondary">
            You signed in with your phone number. Add an email address so you can
            always get back in, even if you turn off our texts.
          </p>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => resetTo('input')}
            className="mt-3"
          >
            Add Email Address
          </Button>
        </div>
      )}

      {step === 'input' && (
        <div>
          <label htmlFor={emailFieldId} className="block text-sm font-medium text-text-secondary mb-1">
            Email Address
          </label>
          <div className="flex gap-2">
            <input
              id={emailFieldId}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleEmailKeyDown}
              aria-invalid={emailError ? true : undefined}
              aria-describedby={emailError ? errorId : undefined}
              className="flex-1 bg-oro-surface border border-oro-border rounded-sm px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:shadow-[0_0_0_2px_rgba(225,195,122,0.2)] focus:outline-none text-sm"
            />
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleRequestCode}
              loading={requestMutation.isPending}
              disabled={busy}
            >
              Send Code
            </Button>
          </div>
          <p className="text-xs text-text-muted mt-1">
            We'll send a 6-digit code to confirm it's yours.
          </p>
          {emailError && (
            <p id={errorId} role="alert" className="text-xs text-oro-error mt-2">{emailError}</p>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            // Distinct accessible name (#2990): this page renders three buttons
            // reading "Cancel" (add-email, phone verify, page-level), which is
            // ambiguous to anyone navigating by control name. Matches the
            // accessibilityLabel the native screen already uses.
            aria-label="Cancel email entry"
            onClick={() => resetTo('idle')}
            className="mt-2"
          >
            Cancel
          </Button>
        </div>
      )}

      {step === 'verify' && (
        <div>
          <label htmlFor={codeFieldId} className="block text-sm font-medium text-text-secondary mb-1">
            Verification Code
          </label>
          <p className="text-xs text-text-muted mb-2">
            Enter the 6-digit code we sent to {pendingEmail}.
          </p>
          <div className="flex gap-2">
            <input
              id={codeFieldId}
              type="text"
              inputMode="numeric"
              // Lets the browser / iOS offer the emailed code straight from the
              // notification instead of making a locked-out user copy it by hand.
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={handleCodeKeyDown}
              aria-invalid={emailError ? true : undefined}
              aria-describedby={emailError ? errorId : undefined}
              className="w-32 bg-oro-surface border border-oro-border rounded-sm px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:shadow-[0_0_0_2px_rgba(225,195,122,0.2)] focus:outline-none text-sm tracking-widest text-center"
            />
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleVerifyCode}
              loading={verifyMutation.isPending}
              disabled={busy || codeInput.length !== 6}
            >
              Verify
            </Button>
          </div>
          {emailError && (
            <p id={errorId} role="alert" className="text-xs text-oro-error mt-2">{emailError}</p>
          )}
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRequestCode}
              loading={requestMutation.isPending}
              disabled={busy}
            >
              Resend Code
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Cancel verification"
              onClick={() => resetTo('idle')}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Account Email section (#1335). A synthetic phone-only account (#2930) has no
 * usable inbox, so the "Change Email" flow can't help it — route those accounts
 * to AddEmailSection instead. Everyone with a real email keeps the change flow,
 * which sends a re-auth link to their current address before allowing a change.
 */
function AccountEmailSection({ email }: { email?: string }) {
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  if (isSyntheticSmsEmail(email)) {
    return <AddEmailSection />;
  }

  const handleRequestChange = async () => {
    setEmailError(null);
    setIsRequesting(true);
    try {
      await authApi.requestEmailChange();
      setRequestSent(true);
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : 'Failed to send verification email. Please try again.';
      setEmailError(message);
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
      <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
        <Mail className="w-5 h-5 text-accent" />
        Account
      </h2>
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">
          Email Address
        </label>
        <p className="text-text-primary text-sm">
          {email}
        </p>
        <p className="text-xs text-text-muted mt-1">
          Used for login and notifications. Only visible to you.
        </p>
      </div>
      <div className="mt-3">
        {requestSent ? (
          <p className="text-sm text-oro-success">
            Verification email sent. Check your inbox.
          </p>
        ) : (
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleRequestChange}
              loading={isRequesting}
              disabled={isRequesting}
            >
              Change Email
            </Button>
            {emailError && (
              <p className="text-xs text-oro-error mt-2">{emailError}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Timezone section — editable dropdown for the user's tz (#1656 read-only,
 * #1658 added edit). Used by the backend to render virtual-event
 * notifications in the recipient's local time. Pre-fills from stored
 * users.timezone (silent capture on login per PR #1652) or, when null,
 * from the current browser tz so a fresh user sees their actual location.
 *
 * Per-section save pattern matches AccountEmailSection / PhoneSection —
 * own state, own action button, no coupling to the giant Personal Info form.
 */
function TimezoneSection({ timezone }: { timezone?: string | null }) {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const stored = timezone || null;

  // Initial picker value: stored tz wins; fall back to browser detection so
  // a user with NULL stored tz sees a useful default rather than "America/
  // New_York" out of the COMMON_TIMEZONES list head.
  const [selectedTz, setSelectedTz] = useState<string>(stored || browserTz);

  // If the parent profile data refreshes (e.g. after a successful save) and
  // the stored value changes, sync the picker. Avoids a stale picker after
  // mutateAsync resolves and ['profile'] is invalidated.
  useEffect(() => {
    if (stored && stored !== selectedTz) {
      setSelectedTz(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored]);

  const updateMutation = useUpdateProfile();

  const dirty = selectedTz !== (stored || '');
  const abbrev = timezoneAbbreviation(selectedTz);
  const drift = stored && stored !== browserTz;

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({ timezone: selectedTz });
    } catch {
      // useOroMutation surfaces error toasts
    }
  };

  // The picker may already include the user's tz (e.g. stored value not in
  // COMMON_TIMEZONES because an admin PATCHed a non-curated zone). Make sure
  // the option is renderable so the picker shows the actual current value.
  const optionsWithCurrent = COMMON_TIMEZONES.some(t => t.value === selectedTz)
    ? COMMON_TIMEZONES
    : [{ value: selectedTz, label: selectedTz }, ...COMMON_TIMEZONES];

  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
      <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5 text-accent" />
        Timezone
      </h2>
      <div className="space-y-2">
        <label htmlFor="user-timezone" className="block text-sm font-medium text-text-secondary">
          Your timezone {abbrev && <span className="text-text-muted font-normal">({abbrev})</span>}
        </label>
        <select
          id="user-timezone"
          value={selectedTz}
          onChange={(e) => setSelectedTz(e.target.value)}
          disabled={updateMutation.isPending}
          className="w-full px-4 py-3 rounded-lg border border-oro-border bg-oro-surface-alt text-text-primary focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors"
        >
          {optionsWithCurrent.map(tz => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
        </select>
        <p className="text-xs text-text-muted">
          Used to render virtual-event notifications in your local time.
          {!stored && ' Defaulted from your browser — save to confirm.'}
        </p>

        {drift && selectedTz === stored && (
          <p className="text-xs text-oro-warning">
            Your browser is currently in <strong>{browserTz}</strong>.{' '}
            <button
              type="button"
              onClick={() => setSelectedTz(browserTz)}
              className="underline hover:text-text-primary transition-colors"
            >
              Switch to {browserTz}
            </button>
          </p>
        )}

        {dirty && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={updateMutation.isPending}
            disabled={updateMutation.isPending}
            className="mt-2"
          >
            Save
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Phone Number section with verification flow (#1461)
 */
function PhoneSection({ phone, phoneVerified }: { phone?: string | null; phoneVerified?: boolean }) {
  const [phoneInput, setPhoneInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [step, setStep] = useState<'idle' | 'input' | 'verify'>('idle');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const requestMutation = useRequestPhoneVerification();
  const verifyMutation = useVerifyPhone();
  const removeMutation = useRemovePhone();

  const handleRequestCode = async () => {
    setPhoneError(null);
    const normalized = normalizePhoneE164(phoneInput);
    if (!normalized) {
      setPhoneError('Enter a valid US/Canada phone number');
      return;
    }
    try {
      await requestMutation.mutateAsync(normalized);
      setStep('verify');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to send code';
      setPhoneError(message);
    }
  };

  const handleVerifyCode = async () => {
    setPhoneError(null);
    try {
      await verifyMutation.mutateAsync(codeInput);
      setStep('idle');
      setPhoneInput('');
      setCodeInput('');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Verification failed';
      setPhoneError(message);
    }
  };

  const handleRemove = async () => {
    setPhoneError(null);
    try {
      await removeMutation.mutateAsync();
      setStep('idle');
      setPhoneInput('');
      setCodeInput('');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to remove phone';
      setPhoneError(message);
    }
  };

  const maskPhone = (p: string) => {
    if (!p || p.length < 6) return p;
    return `${p.slice(0, 2)}\u2022\u2022\u2022\u2022\u2022${p.slice(-4)}`;
  };

  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
      <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
        <Smartphone className="w-5 h-5 text-accent" />
        Phone Number
      </h2>

      {/* Verified phone display */}
      {phone && phoneVerified && step === 'idle' && (
        <div>
          <div className="flex items-center gap-2">
            <p className="text-text-primary text-sm">{maskPhone(phone)}</p>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-oro-success-soft text-oro-success rounded-full">
              <Check className="w-3 h-3" />
              Verified
            </span>
          </div>
          <p className="text-xs text-text-muted mt-1">
            Used for SMS notifications. Only visible to you.
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => { setStep('input'); setPhoneInput(''); }}
            >
              Change
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              loading={removeMutation.isPending}
              disabled={removeMutation.isPending}
            >
              Remove
            </Button>
          </div>
        </div>
      )}

      {/* No phone — show add prompt */}
      {(!phone || !phoneVerified) && step === 'idle' && (
        <div>
          <p className="text-sm text-text-secondary">
            Add a phone number to receive SMS event reminders and notifications.
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setStep('input')}
            className="mt-3"
          >
            Add Phone Number
          </Button>
        </div>
      )}

      {/* Phone input step */}
      {step === 'input' && (
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Phone Number
          </label>
          <div className="flex gap-2">
            <input
              type="tel"
              placeholder="(555) 123-4567"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              className="flex-1 bg-oro-surface border border-oro-border rounded-sm px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:shadow-[0_0_0_2px_rgba(225,195,122,0.2)] focus:outline-none text-sm"
            />
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleRequestCode}
              loading={requestMutation.isPending}
              disabled={requestMutation.isPending}
            >
              Send Code
            </Button>
          </div>
          <p className="text-xs text-text-muted mt-1">US/Canada numbers only. Message and data rates may apply.</p>
          {phoneError && <p className="text-xs text-oro-error mt-2">{phoneError}</p>}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => { setStep('idle'); setPhoneError(null); }}
            className="mt-2"
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Code verification step */}
      {step === 'verify' && (
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Verification Code
          </label>
          <p className="text-xs text-text-muted mb-2">
            Enter the 6-digit code sent to your phone.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-32 bg-oro-surface border border-oro-border rounded-sm px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:shadow-[0_0_0_2px_rgba(225,195,122,0.2)] focus:outline-none text-sm tracking-widest text-center"
            />
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleVerifyCode}
              loading={verifyMutation.isPending}
              disabled={verifyMutation.isPending || codeInput.length !== 6}
            >
              Verify
            </Button>
          </div>
          {phoneError && <p className="text-xs text-oro-error mt-2">{phoneError}</p>}
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRequestCode}
              loading={requestMutation.isPending}
              disabled={requestMutation.isPending}
            >
              Resend Code
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setStep('idle'); setPhoneError(null); setCodeInput(''); }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Builds the form state from a profile. Shared by the lazy initializer (cached
// case) and the seed-once effect (async-arrival case) so the two paths can't
// drift. Mirrors native's `toFormState` in mobile/app/(protected)/settings/profile.tsx.
function toProfileFormData(profile: UserProfile | undefined): ProfileUpdateRequest {
  return {
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    public_name_mode: profile?.public_name_mode || 'full',
    tagline: profile?.tagline || '',
    birthday_day: profile?.birthday_day || null,
    birthday_month: profile?.birthday_month || null,
    location: profile?.location || '',
    primary_sport: profile?.primary_sport || '',
    secondary_sports: profile?.secondary_sports || [],
  };
}

export default function ProfileSettingsPage() {
  const navigate = useNavigate();
  const { data: profile, isLoading, error: profileError, refetch } = useProfile();
  const updateProfileMutation = useUpdateProfile();
  const { mutate: removeProfileImage, isPending: isRemoving } = useRemoveProfileImage();
  const { uploadImage, isUploading, error: uploadError, reset: resetUpload } = useProfileImageUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Seed ONCE (lazy init covers the cached case; the effect below covers async
  // arrival). Never re-seed on refetch → unsaved edits survive a ['profile']
  // cache write (phone verify/remove, email verify, background refetch). Mirrors
  // the seededRef guard in mobile/app/(protected)/settings/profile.tsx. #2992.
  const [formData, setFormData] = useState<ProfileUpdateRequest>(() => toProfileFormData(profile));

  const [selectedSecondaryBits, setSelectedSecondaryBits] = useState<Set<string>>(
    () => new Set(profile?.secondary_sports || [])
  );
  const seededRef = useRef(!!profile);
  const [error, setError] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  useEffect(() => {
    if (profile && !seededRef.current) {
      seededRef.current = true;
      /* eslint-disable react-hooks/set-state-in-effect */
      setFormData(toProfileFormData(profile));
      setSelectedSecondaryBits(new Set(profile.secondary_sports || []));
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [profile]);

  const toggleSecondarySport = (sport: string) => {
    setSelectedSecondaryBits(prev => {
      const has = prev.has(sport);
      // Cap at MAX_SECONDARY_SPORTS (#2698). This is the AUTHORITATIVE guard on the
      // state invariant — the disabled chip below is only a UX affordance. Without
      // it, adding a 6th secondary sends secondary_sports over UpdateProfileSchema's
      // .max(5) and the whole save 400s (worse now the list is 17). Deselect always
      // works. Mirrors ActivitiesStep's toggleSport.
      if (!has && prev.size >= MAX_SECONDARY_SPORTS) {
        return prev;
      }
      const newSet = new Set(prev);
      if (has) {
        newSet.delete(sport);
      } else {
        newSet.add(sport);
      }
      setFormData(prevData => ({
        ...prevData,
        secondary_sports: Array.from(newSet)
      }));
      return newSet;
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const maxSize = 20 * 1024 * 1024; // 20MB - CF Image Resizing handles optimization (#792)
    if (file.size > maxSize) {
      toast.error('Image must be under 20MB');
      return;
    }

    // Show crop modal for user to crop the image
    setPendingFile(file);
    setShowCropModal(true);
  };

  const handleCropComplete = async (croppedFile: File) => {
    setShowCropModal(false);
    setPendingFile(null);
    
    try {
      await uploadImage(croppedFile);
      // Success - the hook will automatically refresh the profile data
    } catch {
      // Error is handled by the hook and displayed in UI
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setPendingFile(null);
  };

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      // Sanitize formData: an emptied nullable field must reach the API as
      // `null` (which clears the column), NOT `undefined` (which JSON.stringify
      // drops, so the key never arrives and the value is never cleared). #2931
      // fixes the #776 regression whose comment said null but code emitted
      // undefined; the mapping is centralized so web + native can't drift.
      const sanitizedData = {
        ...formData,
        ...normalizeClearableProfileFields(formData),
      };
      await updateProfileMutation.mutateAsync(sanitizedData);
      navigate('/settings');
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : 'Failed to save profile. Please try again.';
      setError(message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Profile Settings" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 pb-20 space-y-6">
          <div className="text-center space-y-4">
            <Skeleton variant="circular" className="w-24 h-24 mx-auto" />
            <Skeleton className="h-8 w-32 mx-auto" />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
          ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty-profile gate. `normalizeClearableProfileFields` turns an unseeded
  // (empty) form into an all-`null` payload, so a Save from a form that never
  // received `profile` wipes tagline/location/primary_sport/secondary_sports
  // (#2931 regression). That all-null wipe requires `profile` to be ABSENT — the
  // seeding effect above leaves a PRESENT profile correctly hydrated, so Save
  // then sends real values. TanStack Query v5 `isLoading` is already false once
  // a query has errored, so the loading branch above does not cover a
  // first-load failure (no cached data → `!profile` true).
  //
  // The gate is `!profile` ALONE — deliberately NOT `profileError || !profile`.
  // TanStack Query v5 RETAINS cached `data` when a *background* refetch fails
  // (offline / 500 / expired session, reachable after the 5-min staleTime +
  // default `refetchOnWindowFocus`) and merely sets `error`. Gating on
  // `profileError` there would unmount a mid-edit form and discard everything
  // the user typed — strictly worse than the failure it guards, and it does not
  // reopen the wipe hazard because the cached profile keeps the form seeded. We
  // still read `profileError` for the MESSAGE so a first-load failure explains
  // itself. NOTE: native
  // (mobile/app/(protected)/settings/profile.tsx:181) still uses the compound
  // `profileQuery.error || !profile`; that pre-existing divergence is out of
  // scope for this PR and tracked separately. Do NOT "restore parity" by
  // reintroducing `profileError ||` here — it silently reintroduces the eject.
  if (!profile) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Profile Settings" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12">
            <EmptyState
              icon={<AlertCircle className="w-12 h-12" />}
              title="Cannot load profile"
              message={
                profileError instanceof ApiError
                  ? profileError.message
                  : 'Something went wrong loading your profile. Please try again.'
              }
              actionLabel="Retry"
              onAction={() => refetch()}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar
        title="Profile Settings"
        onBack={() => navigate(-1)}
      />

      <div className="flex-1 overflow-y-auto pt-14">
        {/* Upload Error Display */}
        {uploadError && (
          <div className="px-4 pt-4">
            <div className="bg-oro-error-soft border border-oro-error-border rounded-lg p-3 flex items-start gap-2">
              <div className="text-oro-error text-sm font-medium flex-1">
                {uploadError}
              </div>
              <button
                onClick={resetUpload}
                className="text-oro-error hover:opacity-70"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="pt-4 px-4 pb-20">
        {/* Profile Picture Section */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
          <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
            <Camera className="w-5 h-5 text-accent" />
            Profile Picture
          </h2>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 bg-oro-surface-alt rounded-full flex items-center justify-center">
                {profile?.profile_image_url ? (
                  <img 
                    src={profile.profile_image_url} 
                    alt="Profile" 
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <User className="w-8 h-8 text-text-muted" />
                )}
              </div>
              <button 
                type="button"
                onClick={handleCameraClick}
                disabled={isUploading}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-oro-accent shadow-lg flex items-center justify-center transition-all z-40 hover:bg-oro-accent/90 hover:scale-110"
              >
                <Camera className={`w-4 h-4 text-white ${isUploading ? 'animate-pulse' : ''}`} />
              </button>
              
              {/* Remove avatar button - only show when avatar exists */}
              {profile?.profile_image_url && (
                <AvatarRemoveButton
                  onRemove={() => removeProfileImage()}
                  isLoading={isRemoving}
                />
              )}
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
            <div className="flex-1">
              <Button 
                variant="secondary" 
                size="sm" 
                type="button"
                onClick={handleCameraClick}
                disabled={isUploading}
              >
                Change Photo
              </Button>
              <p className="text-xs text-text-muted mt-2">
                JPG or PNG, max 20MB
              </p>
            </div>

            {/* Upload crop modal */}
            {showCropModal && pendingFile && (
              <AvatarCropModal
                isOpen={showCropModal}
                imageFile={pendingFile}
                onCropComplete={handleCropComplete}
                onClose={handleCropCancel}
              />
            )}
          </div>
        </div>

        {/* Account Email (#1334, #1335) */}
        <AccountEmailSection email={profile?.email} />

        {/* Phone Number (#1461) */}
        <PhoneSection phone={profile?.phone} phoneVerified={profile?.phone_verified} />

        {/* Timezone (#1656) */}
        <TimezoneSection timezone={profile?.timezone} />

        {/* Personal Information */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
          <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-accent" />
            Personal Information
          </h2>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  First Name *
                </label>
                <Input
                  value={formData.first_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="John"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Last Name *
                </label>
                <Input
                  value={formData.last_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Smith"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Public Name Display
              </label>
              <select
                value={formData.public_name_mode || 'full'}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  public_name_mode: e.target.value as typeof formData.public_name_mode 
                }))}
                className="w-full px-4 py-2 text-sm border border-oro-border rounded-lg bg-oro-surface-alt text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="full">Full Name ({formData.first_name || 'John'} {formData.last_name || 'Smith'})</option>
                <option value="first_last_initial">First + Initial ({formData.first_name || 'John'} {(formData.last_name || 'Smith').charAt(0)}.)</option>
                <option value="first_only">First Name Only ({formData.first_name || 'John'})</option>
                {/* Issue #919: Anonymous disabled for v1.0 */}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Bio
              </label>
              <textarea
                value={formData.tagline || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, tagline: e.target.value }))}
                placeholder="Tell others about yourself..."
                rows={3}
                className="w-full px-4 py-2 text-sm border border-oro-border rounded-lg bg-oro-surface-alt text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Location
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
                <Input
                  value={formData.location || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Los Angeles, CA"
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Birthday */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
          <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-accent" />
            Birthday (Optional)
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Month
              </label>
              <select
                value={formData.birthday_month?.toString() || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  birthday_month: e.target.value ? parseInt(e.target.value) : null 
                }))}
                className="w-full px-4 py-2 text-sm border border-oro-border rounded-lg bg-oro-surface-alt text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Select month</option>
                {MONTHS.map((month, index) => (
                  <option key={month} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Day
              </label>
              <Input
                type="number"
                value={formData.birthday_day?.toString() || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  birthday_day: e.target.value ? parseInt(e.target.value) : null 
                }))}
                placeholder="15"
                min="1"
                max="31"
              />
            </div>
          </div>
          
          <p className="text-xs text-text-muted mt-2">
            Year is not required. This helps with birthday notifications.
          </p>
        </div>

        {/* Sports Preferences */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
          <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-accent" />
            Sports & Interests
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Primary Sport
              </label>
              <select
                value={formData.primary_sport || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, primary_sport: e.target.value }))}
                className="w-full px-4 py-2 text-sm border border-oro-border rounded-lg bg-oro-surface-alt text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Select a sport</option>
                {ALLOWED_SPORTS.map((sport: string) => (
                  <option key={sport} value={sport}>
                    {sport}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Other Sports (Select up to {MAX_SECONDARY_SPORTS})
              </label>
              <div className="grid grid-cols-2 gap-2">
                {ALLOWED_SPORTS.filter(s => s !== formData.primary_sport).map((sport: string) => {
                  const isSelected = selectedSecondaryBits.has(sport);
                  const isDisabled = selectedSecondaryBits.size >= MAX_SECONDARY_SPORTS && !isSelected;
                  return (
                    <button
                      key={sport}
                      type="button"
                      onClick={() => toggleSecondarySport(sport)}
                      disabled={isDisabled}
                      className={`p-2 rounded-lg border text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        isSelected
                          ? 'bg-accent/10 border-accent text-accent'
                          : 'bg-oro-surface-alt border-oro-border text-text-secondary hover:border-accent/60'
                      }`}
                    >
                      {sport}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 rounded-lg bg-oro-error-soft border border-oro-error-border flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-oro-error shrink-0 mt-0.5" />
            <p className="text-sm text-oro-error">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={updateProfileMutation.isPending}
            disabled={updateProfileMutation.isPending}
          >
            <Save className="w-5 h-5 mr-2" />
            Save Changes
          </Button>
          
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={() => navigate('/settings')}
            disabled={updateProfileMutation.isPending}
          >
            <X className="w-5 h-5 mr-2" />
            Cancel
          </Button>
        </div>
        </form>
      </div>
    </div>
  );
}
